import type { CategoryId, TagId, TriedRatingValue, ReviewRatingValue } from '@/data';

// Review from a reviewer
export interface ClipReview {
  reviewerId: string;
  rating: ReviewRatingValue | string;
  comment: string;
  date: string;
  // Load metrics for male-focused clips (optional for backwards compat)
  cumLevel?: number;      // 1-5 volume rating
  cumConsistency?: number; // 1-5 consistency rating
  // Her metrics for female-focused clips
  wetnessLevel?: number;  // 1-5 wetness rating
  squirtLevel?: number;   // 0-5 squirting rating (0 = no squirting)
}

// Couple's rating of a clip they tried
export interface CoupleRating {
  rating: TriedRatingValue;
  date: string;
}

// A content clip
export interface Clip {
  id: string;
  categoryId: CategoryId | string;
  additionalCategoryIds?: string[]; // For multi-category clips
  title: string;
  link: string;
  description: string;
  tags: (TagId | string)[];
  rating: string;
  notesForHim: string;
  notesForHer: string;
  reviews: ClipReview[];
  picks: string[]; // reviewer IDs who picked this
  // Couple engagement stats
  couplesTried?: number;
  couplesRatings?: CoupleRating[];
}

// Helper to get all category IDs for a clip (primary + additional)
export const getClipCategoryIds = (clip: Clip): string[] => {
  const ids = [clip.categoryId];
  if (clip.additionalCategoryIds?.length) {
    ids.push(...clip.additionalCategoryIds);
  }
  return [...new Set(ids)]; // Remove duplicates
};

// Creator who submitted content
export interface Creator {
  id: string;
  product: 'Load Boost' | 'Soaking Wet';
  orderId: string;
  purchaseDate: string;
  registeredAt: string;
}

// Reviewer's "try at home" assignment
export interface TryAtHomeAssignment {
  clipId: string;
  assignedAt: string;
  completedAt?: string;
  feedback?: string;
  rating?: string;
}

// Homework history entry
export interface HomeworkEntry {
  clipId: string;
  assignedAt: string;
  completedAt?: string;
  rating?: string;
  feedback?: string;
  // Media capture questions
  tookMedia?: 'yes' | 'no';
  willingToShare?: 'yes' | 'maybe' | 'no';
  mediaUrl?: string; // If they share the URL
  comparisonToClip?: string; // How it compared to the clip
}

// Activity creator/modifier tracking
export interface ActivityActor {
  type: 'reviewer' | 'couple' | 'admin';
  id: string;
  name?: string;
}

// Scheduled activity for calendar planning
export interface ScheduledActivity {
  id: string;
  coupleId?: string;
  clipId: string;
  scheduledDate: string; // ISO date string (YYYY-MM-DD)
  createdAt: string;
  completedAt?: string;
  notes?: string;
  reminder?: boolean;
  createdBy?: ActivityActor;
  modifiedBy?: ActivityActor;
  modifiedAt?: string;
  userClipUrl?: string; // User-submitted clip URL on completion
}

// Couple preference rating from onboarding
export interface CouplePreference {
  clipId: string;
  categoryId: string;
  clipTitle: string;
  rating: 'try' | 'maybe' | 'skip';
  ratedAt: string;
}

// Reviewer who rates clips
export interface Reviewer {
  id: string;
  code: string;
  name?: string; // Display name (optional - shown to couples they manage)
  registeredAt: string;
  // Couple integration - OWN profile
  coupleId?: string; // VBC-XXXXXX - reviewer's OWN couple profile for Date Night mode
  partnerName?: string;
  couplePreferences?: CouplePreference[]; // Preferences set during onboarding
  mode?: 'reviewer' | 'couple'; // Current view mode for unified experience
  // Managed couple - EXTERNAL couple they coach/manage (separate from own profile)
  managedCoupleId?: string; // VBC-XXXXXX - external couple this reviewer manages
  // Current homework
  tryAtHome?: TryAtHomeAssignment;
  // Homework history
  homeworkHistory?: HomeworkEntry[];
  // Calendar scheduled activities
  scheduledActivities?: ScheduledActivity[];
  // Pleasure playlist (auto-curated from highest-rated reviews)
  pleasurePlaylist?: {
    clips: string[];              // Ordered clip IDs (persisted)
    enjoyed: Record<string, {
      enjoyedAt: string;
      reaction?: string;           // emoji reaction
    }>;
    skipped: string[];
    savedForLater: string[];      // Playlist-internal bookmarks
    generatedAt: string;
  };
  // Category champions (head-to-head matchups per category)
  categoryChampions?: {
    matchups: { categoryId: string; winnerId: string; loserId: string; decidedAt: string; reason?: string }[];
    champions: Record<string, string>; // categoryId -> winning clipId
    pendingOrder: Record<string, string[]>; // categoryId -> remaining clip IDs to face
    currentKing: Record<string, string>; // categoryId -> current reigning clip ID
    rankings: Record<string, string[]>; // categoryId -> ordered clip IDs (1st = best)
    seenClips: Record<string, string[]>; // categoryId -> clip IDs that have been through tournament
  };
  // Dismissed prompts (persisted to avoid showing again)
  dismissedPrecumPrompts?: string[]; // Array of coupleIds where precum prompt was dismissed
  // Section A: Background
  gender: 'Male' | 'Female' | 'Non-binary' | 'Prefer not to say' | string;
  ageRange: '18-24' | '25-34' | '35-44' | '45-54' | '55+' | string;
  relationshipStatus: string;
  sexuallyActive?: string;
  watchesAdultContent: string;
  adultContentThemes?: string;
  adultContentExample?: string;
  whatMakesGoodContent?: string;
  whatFeelsFake?: string;
  preferredStyle?: string;
  familiarWithProducts: string;
  relevantExperience?: string;
  whyInterested?: string;
  // Section B: Load Boost
  enjoysVolumeContent: string;
  // Male specific
  triedLoadBoost?: string;
  loadBoostExperience?: string;
  partnerTypicalVolume?: string;
  partnerVolumeLikeDislike?: string;
  // Female specific
  wantPartnerLoadBoost?: string;
  enjoysSwallowing?: string;
  swallowingDescription?: string; // Describe experience if affirmative
  wouldEnjoyBetterTaste?: string;
  enjoysFacials?: string;
  volumeMakesDifference?: string;
  // Section C: Soaking Wet
  // Female specific
  canSquirt?: string;
  squirtScenarioDescription?: string;
  wantToSquirtMore?: string;
  usedArousalProducts?: string;
  // Wetness & orgasm questions (female)
  orgasmFromSex?: string; // always/usually/sometimes/rarely/never
  typicalWetness?: number; // 1-5 scale
  wetnessNotes?: string;
  wantToBeWetter?: string; // yes/maybe/no/happy as is
  // Male specific
  enjoysSquirtingContent?: string;
  wantMoreWithPartner?: string;
  partnerUsedArousalProducts?: string;
  // General
  additionalNotes?: string;
  // Legacy compatibility
  watchesContent?: string;
  // Section D: Sexual Experience Details
  lastSexualActivity?: string; // Describe your last sexual activity
  facialExperience?: string; // Have you had/given facials? Describe
  oralExperience?: string; // Describe oral experiences (giving/receiving)
  analExperience?: string; // Have you tried anal? Describe
  favoriteSexualMemory?: string; // Most memorable/favorite sexual experience
  fantasiesNotTried?: string; // Fantasies you haven't tried yet
  biggestTurnOns?: string; // What turns you on most
  dealBreakers?: string; // What turns you off / dealbreakers
  // Section E: Couple Experience Interest
  interestedInCoupleExperience?: string;
  wouldSubmitClips?: string;
  comfortWithAnonymousFeedback?: string;
  interestedInScheduledActivities?: string;
  interestedInCuratedExperiences?: string;
  partnerSizePreference?: string;
  partnerSizeDescription?: string;
  wetnessImportance?: string;
  openToNewThings?: string;
  coupleExperienceNotes?: string;
}

// A clip that was tried by a couple
export interface TriedClip {
  clipId: string;
  rating: TriedRatingValue;
  hisThoughts: string;
  herThoughts: string;
  triedAt: string;
  // Enhanced feedback questions
  wouldDoAgain?: 'yes' | 'maybe' | 'no';
  inspiresDifferent?: 'yes' | 'no';
  howDifferent?: string; // If inspiresDifferent is 'yes'
  // Cum/load feedback (shown for relevant categories: cum, oral, squirt, wet)
  cumVolume?: number;       // 1-5 (matches HIS_CUM_SCALE)
  cumConsistency?: number;  // 1-5 (matches HIS_CONSISTENCY_SCALE)
  cumTaste?: number;        // 1-5 (matches HIS_TASTE_SCALE)
  cumNotes?: string;
}

// Reflection from one partner on a scheduled activity
export interface ActivityReflection {
  howItWent: string;
  differenceFromClip: string;
  nextTimeDifferently: string;
  rating: TriedRatingValue;
}

// His load feedback for post-event survey
export interface HisLoadFeedback {
  volume?: number;      // 1-5 scale
  thickness?: number;   // 1-5 scale
  taste?: number;       // 1-5 scale
  distance?: number;    // 1-5 scale (how far it shot)
  intensity?: number;   // 1-5 scale (orgasm intensity)
  notes?: string;       // Any additional notes
}

// Follow-up survey for a scheduled activity
export interface ActivityFollowUp {
  id: string;
  scheduledActivityId: string;
  clipId: string;
  coupleId: string;
  scheduledDate: string;
  clipTitle?: string; // Denormalized for display

  // Response
  didComplete: boolean;

  // If completed - one person answers for both perspectives
  hisReflection?: ActivityReflection;
  herReflection?: ActivityReflection;
  hisLoadFeedback?: HisLoadFeedback;

  // If not completed
  notCompletedReason?: string;
  rescheduledToActivityId?: string;

  // Metadata
  promptedAt: string;
  respondedAt?: string;
  respondedBy?: 'him' | 'her' | 'both';
  status: 'pending' | 'completed' | 'dismissed';
}

// Preference for a category
export type CategoryPreference = 'love' | 'like' | 'curious' | 'nope' | 'neutral';

// His profile stats (optional, fun visuals)
export interface HisProfile {
  size?: number;        // 1-6 (Sm to WOW)
  girth?: number;       // 1-5 (Slim to Wow)
  cumIntensity?: number; // 1-5 (Drip to TSUNAMI)
  // Consistency and taste metrics
  cumConsistency?: number; // 1-5 (Watery to Creamy)
  consistencyNotes?: string;
  cumTaste?: number;       // 1-5 (Bad to Yum)
  tasteNotes?: string;
  // Precum production
  precumProduction?: number; // 1-5 (None to Dripping)
  precumNotes?: string;
  // Visual sharing preferences (optional prompts)
  visualSharingOpen?: 'yes' | 'maybe' | 'no';
  visualUrls?: {
    sizeUrl?: string;
    cumUrl?: string;
    precumUrl?: string;
    otherUrl?: string;
  };
  visualSharingNotes?: string;
}

// Her profile stats (optional, fun visuals)
export interface HerProfile {
  typicalWetness?: number;    // 1-5 (Light to Soaking)
  wetnessNotes?: string;
  squirtingAbility?: number;  // 0-5 (Never to Gushing)
  squirtingNotes?: string;
  sensitivity?: number;       // 1-5 (Low to Ultra)
  sensitivityNotes?: string;
  orgasmEase?: number;        // 1-5 (Difficult to Multiple)
  orgasmNotes?: string;
  // Visual sharing preferences (optional prompts)
  visualSharingOpen?: 'yes' | 'maybe' | 'no';
  visualUrls?: {
    wetnessUrl?: string;
    squirtUrl?: string;
    otherUrl?: string;
  };
  visualSharingNotes?: string;
}

// Couple's saved data
export interface CoupleData {
  coupleId: string; // Shareable code for recommendations like VBC-XXXXXX
  accessCode: string; // Private login code like VBA-XXXXXX
  hisName: string;
  herName: string;
  setupAt: string;
  preferences: Record<string, CategoryPreference>;
  tried: TriedClip[];
  favorites: string[]; // clip IDs
  favoriteNotes?: Record<string, string>; // clipId -> why they liked it
  hisProfile?: HisProfile; // Optional fun stats for him
  herProfile?: HerProfile; // Optional fun stats for her
  // Profile management fields
  managedBy?: 'him' | 'her' | 'both'; // Who manages the profile
  // Linked reviewer who manages this couple
  managedByReviewerId?: string; // Reviewer ID who is coaching this couple
  managedByReviewerName?: string; // Reviewer's display name
  managedByAcceptedAt?: string; // When the reviewer accepted to manage
  lastActivityAt?: string; // ISO timestamp of last engagement
  profileNotes?: string; // General notes (500 char limit)
  // Feedback/onboarding fields
  wouldSubmitClips?: 'yes' | 'maybe' | 'no'; // Would they submit their own clips?
  productExcitement?: 1 | 2 | 3 | 4 | 5; // How excited about the product (1-5 stars)
  generalFeedback?: string; // Optional freeform feedback
  feedbackSubmittedAt?: string; // When feedback was last submitted
  // Scheduling preferences
  availableDays?: number[]; // 0=Sun, 1=Mon, ..., 6=Sat - days when activities can be scheduled
  // Category-specific Q&A for personalized recommendations
  categoryQA?: Record<string, CategoryQAResponse>; // categoryId -> their answers
  // Couple experience preferences
  interestedInAnonymousFeedback?: string;
  comfortWithFeedback?: string;
  interestedInScheduledActivities?: string;
  interestedInCuratedExperiences?: string;
  hisSizeDescription?: string;
  wetnessImportance?: string;
  openToNewThings?: string;
  coupleExperienceNotes?: string;
  contentRequestPreferences?: string; // e.g., "Currently only taking solo requests"
}

// Category Q&A response for personalized content curation
export interface CategoryQAResponse {
  categoryId: string;
  answeredAt: string;
  answers: Record<string, string>; // questionId -> answer text
}

// A recommendation from someone for a couple
export interface CoupleRecommendation {
  id: string;
  coupleId: string;
  clipId: string;
  fromName: string;
  message: string;
  createdAt: string;
  seen: boolean;
  coupleReply?: string;
  coupleRepliedAt?: string;
}

// Chat message between couple and reviewer
export interface CoupleMessage {
  id: string;
  coupleId: string;
  fromType: 'couple' | 'reviewer' | 'system';
  fromName: string;
  message: string;
  createdAt: string;
  seenByCouple: boolean;
  seenByReviewer: boolean;
  // Optional: shared clip reference
  clipId?: string;
  // Optional: link type for system messages (e.g., 'pick-for-us')
  systemType?: 'pick-for-us' | 'scheduled' | 'completed';
}

// Couple's video submission (their own content)
export interface CoupleSubmission {
  id: string;
  coupleId: string;
  videoUrl: string;
  title: string;
  description?: string;
  supplements?: string[];  // Which VB products used
  status: 'pending' | 'reviewed' | 'approved' | 'needs_work';
  createdAt: string;
  reviewedAt?: string;
  reviewedBy?: string;     // Reviewer ID (legacy, single reviewer)
  reviewedByIds?: string[]; // All reviewer IDs who have reviewed this
  reviewerNote?: string;   // Feedback from reviewer
}

// Couple's content request (what they want to see)
export interface CoupleRequest {
  id: string;
  coupleId: string;
  type: 'clip' | 'category';
  description: string;
  categoryId?: string;     // For clip requests - related category
  exampleUrl?: string;     // RedGifs example link (legacy single link)
  exampleUrls?: string[];  // Multiple example links for category requests
  categoryName?: string;   // For category requests - suggested category name
  whyWanted?: string;      // Why they want this content/category
  status: 'pending' | 'fulfilled' | 'declined';
  createdAt: string;
  fulfilledAt?: string;
  fulfilledBy?: string;    // Reviewer ID
  responseNote?: string;   // Reviewer's response
  fromReviewerId?: string;   // If request is FROM reviewer TO couple
  fromReviewerName?: string; // Reviewer's display name
}

// Review data for a submitted clip
export interface SubmittedClipReview {
  reviewerId: string;
  reviewedAt: string;
  // Compliance checklist
  onBrand: boolean;
  showsProduct: boolean;
  claimsCompliant: boolean;
  // Quality (1-5)
  qualityRating: number;
  // Gut reaction
  wouldTryProduct: 'yes' | 'maybe' | 'no' | 'already-use';
  wouldShowPartner: 'yes' | 'maybe' | 'no' | 'na';
  wouldShareWithFriend: 'yes' | 'maybe' | 'no';
  // Enjoyment & reaction
  enjoyedIt?: 'loved-it' | 'enjoyed-it' | 'neutral' | 'not-for-me';
  arousedThem?: 'very' | 'somewhat' | 'not-really' | 'no';
  howItMadeThemFeel?: string;
  // What intrigues / stands out to the reviewer
  intrigues?: string;
  // Value & interest signals
  wouldPayToView?: 'yes' | 'maybe' | 'no';
  wouldSubscribe?: 'yes' | 'maybe' | 'no';
  wantMoreFromCreator?: 'yes' | 'maybe' | 'no';
  wouldWatchMaleSolo?: 'yes' | 'maybe' | 'no';
  // Categories this content would fit well in
  suggestedCategories?: string[];
  // Feedback
  notes: string;
  revisionRequests?: string;
  creatorSuggestions?: string;
  // Follow-up questions suggested based on review responses
  followUpSuggestions?: string[];
  // Second-stage reaction after seeing other reviewers' takes
  postRevealChangedMind?: 'same' | 'somewhat' | 'changed';
  postRevealRating?: number;
  postRevealNotes?: string;
  postRevealReasons?: string[];
  // Per-reviewer assessments (so each reviewer's view is preserved)
  maleAppearance?: MaleAppearanceProfile;
  loadBoostProfile?: LoadBoostProfile;
  // Reviewer's recommendation; the admin makes the final status decision
  verdict?: SubmissionVerdict;
}

export type SubmissionVerdict = 'approve' | 'revise' | 'reject';

// Load Boost specific profile for submitted clips
export interface LoadBoostProfile {
  consistencyType: 'spray-watery' | 'thick-ropey' | 'mixed' | 'not-visible';
  finishLocation: string;
  standoutNotes: string;
}

// Male appearance assessment for submitted clips
export interface MaleAppearanceProfile {
  cockSize: 'small' | 'average' | 'above-average' | 'large' | 'huge';
  hardness: 'soft' | 'semi' | 'hard' | 'rock-hard';
  notes?: string;
}

// Question from a reviewer to the creator about a submission (visible to admin + reviewer + creator)
export interface ReviewerQuestion {
  id: string;
  reviewerId: string;
  question: string;
  askedAt: string;
  reply?: string;
  repliedAt?: string;
  repliedBy?: string;  // creator who replied
}

// User-submitted clip (from creators)
export interface SubmittedClip {
  id: string;
  clipTitle: string;
  contentLink: string;
  creatorId: string;
  product: string;
  productsTaking?: string;  // What supplements/products they've been taking
  gender?: 'male' | 'female' | 'couple';
  status: 'Submitted' | 'Under Review' | 'Approved' | 'Needs Revision' | 'Rejected';
  dateSubmitted: string;
  standoutNotes: string;
  // Review data (legacy single review - kept for backward compat)
  review?: SubmittedClipReview;
  // Multiple reviewer reviews
  reviews?: SubmittedClipReview[];
  // Reviewer questions (visible to admin + asking reviewer)
  reviewerQuestions?: ReviewerQuestion[];
  // Product-specific fields
  loadBoostProfile?: LoadBoostProfile;
  maleAppearance?: MaleAppearanceProfile;
  // Tracking
  promotedToClipId?: string;  // Links to DateNite Clip if promoted
}

// Tracks when a reviewer views a clip
export interface ClipView {
  clipId: string;
  viewerId: string;
  viewerName: string;
  viewedAt: string;
  source: 'datenite' | 'submissions';
}

// Custom follow-up question from admin to reviewer
export interface ReviewerFollowUp {
  id: string;
  reviewerId: string;
  question: string;
  context?: string; // Why we're asking
  sentAt: string;
  answer?: string;
  answeredAt?: string;
  status: 'pending' | 'answered' | 'dismissed' | 'queued'; // 'queued' = waiting for previous in sequence
  // For sequential questions (queue system)
  sequenceId?: string;    // Groups questions that should be sent in order
  sequenceOrder?: number; // Position in sequence (0, 1, 2...) - 0 is sent first, others wait
  // Threading support
  parentId?: string; // ID of the question this is replying to (for threaded conversations)
  // Optional: linked content
  clipId?: string;        // If this question relates to a specific clip (legacy single)
  clipIds?: string[];     // Multiple clips attached to this question/reply
  // Edit suggestion outcome notification (if this is an edit notification)
  editOutcome?: {
    suggestedEditId: string;
    clipId: string;
    clipTitle: string;
    outcome: 'approved' | 'rejected';
    rejectionReason?: string;
  };
}

// Question from reviewer to couple based on profile/activities
export interface CoupleQuestion {
  id: string;
  coupleId: string;
  fromReviewerId: string;
  fromReviewerName: string;
  question: string;
  category?: 'size' | 'girth' | 'cum' | 'precum' | 'general' | 'activity'; // What triggered the question
  context?: string; // Why asking (e.g., "Based on your profile")
  sentAt: string;
  answer?: string;
  answeredAt?: string;
  answeredBy?: string; // 'him' | 'her' | 'both'
  status: 'pending' | 'answered' | 'dismissed';
  // Threading support
  parentId?: string; // ID of the question this is replying to (for threaded conversations)
  // Optional: linked content
  clipId?: string; // If this question relates to or includes a specific clip (legacy single)
  clipIds?: string[]; // Multiple clips attached to this question/reply
}

// AI follow-up question from admin to couple (like ReviewerFollowUp but for couples)
export interface CoupleFollowUp {
  id: string;
  coupleId: string;
  question: string;
  context?: string; // Why we're asking (e.g., "Based on your preference for X")
  sentAt: string;
  answer?: string;
  answeredAt?: string;
  answeredBy?: 'him' | 'her' | 'both';
  status: 'pending' | 'answered' | 'dismissed' | 'queued';
  // For sequential questions (queue system)
  sequenceId?: string;    // Groups questions that should be sent in order
  sequenceOrder?: number; // Position in sequence (0, 1, 2...) - 0 is sent first, others wait
  // Threading support
  parentId?: string; // ID of the question this is replying to (for threaded conversations)
  // Optional: linked content
  clipId?: string;        // If this question relates to a specific clip (legacy single)
  clipIds?: string[];     // Multiple clips attached to this question/reply
  // Links shared by couple (e.g., video URLs in response to clip requests)
  sharedLinks?: { url: string; label?: string }[];
  // Reviewer response/follow-up
  reviewerResponse?: string;
  reviewerRespondedAt?: string;
  reviewerResponderId?: string;
  reviewerResponderName?: string;
}

// Individual clip in a suggestion request
export interface ClipSuggestionItem {
  id: string;
  url: string;           // Redgif or other video link
  title: string;
  description?: string;  // Optional context about this specific clip
  // Response fields (filled by couple)
  rating?: number;       // 1-5 stars
  comment?: string;      // Their thoughts
  interested?: boolean;  // Would they want to try this?
  respondedAt?: string;
}

// Clip suggestion request from admin to couple
export interface ClipSuggestionRequest {
  id: string;
  coupleId?: string;     // Target couple (for couple suggestions)
  reviewerId?: string;   // Target reviewer (for reviewer clip review requests)
  context: string;       // "Following up on your interest in MFM..."
  clips: ClipSuggestionItem[];
  sentAt: string;
  status: 'pending' | 'partial' | 'completed';
  completedAt?: string;
  // Link to original Q&A thread (optional)
  linkedQuestionId?: string;
}

// "Pick for us" request - couple selects clips for reviewer to choose from
export interface PickForUsItem {
  clipId: string;
  clipTitle: string;
  note?: string;         // Couple's note about why they're interested
}

export interface PickForUsRequest {
  id: string;
  coupleId: string;      // Couple who submitted
  reviewerId: string;    // Reviewer who will pick
  items: PickForUsItem[];
  submittedAt: string;
  status: 'pending' | 'picked' | 'scheduled' | 'declined';
  // Reviewer's response
  pickedClipId?: string;
  pickedAt?: string;
  reviewerNote?: string;
  scheduledActivityId?: string;  // Links to scheduled activity if scheduled
  declinedReason?: string;
}

// Female Visual Survey - clip examples for each wetness/squirting level
export interface FemaleSurveyLevelConfig {
  level: number;           // 0-5 (0 only for squirting = never)
  label: string;           // e.g., "Light moisture", "Soaking wet"
  description: string;     // What this level looks like
  clipId?: string;         // Reference clip ID from library
  clipUrl?: string;        // Or direct URL if not in library
}

export interface FemaleSurveyConfig {
  wetness: FemaleSurveyLevelConfig[];    // Levels 1-5
  squirting: FemaleSurveyLevelConfig[];  // Levels 0-5
  updatedAt?: string;
}

export interface FemaleSurveyResponse {
  id: string;
  coupleId: string;
  herName: string;
  wetness: {
    currentLevel: number;   // 1-5
    goalLevel?: number;     // 1-5 (optional - where she wants to be)
    notes?: string;
  };
  squirting: {
    currentLevel: number;   // 0-5 (0 = never squirted)
    goalLevel?: number;     // 0-5
    notes?: string;
  };
  completedAt: string;
  updatedAt?: string;
}

// Couple's shared wishlist item (Want to Try)
export interface WishlistItem {
  id: string;
  coupleId: string;  // Shared between couple
  clipId: string;
  clipTitle: string;
  addedBy: string;   // Reviewer/person who added it
  addedAt: string;
  status: 'want-to-try' | 'tried' | 'not-interested';
  triedAt?: string;
  notes?: string;
}

// Suggested edit from reviewer (pending admin approval)
export interface SuggestedEdit {
  id: string;
  clipId: string;
  reviewerId: string;
  reviewerName: string;
  submittedAt: string;
  status: 'pending' | 'approved' | 'rejected';
  // What reviewer wants to change
  suggestedChanges: {
    title?: string;
    description?: string;
    tags?: string[];
    categoryId?: string;
    additionalCategoryIds?: string[];
    link?: string;
  };
  // Optional context from reviewer
  reason?: string;
  // Admin response (filled on review)
  reviewedAt?: string;
  reviewedBy?: string;
  rejectionReason?: string;
}

// Tag tutorial for reviewer education
export interface TagTutorial {
  id: string;
  reviewerId: string;
  tagId: string;           // Base tag being taught (e.g., 'asian', 'amateur')
  status: 'unread' | 'viewed' | 'completed';
  sentAt: string;
  viewedAt?: string;
  completedAt?: string;
  // Admin assignment
  assignedBy?: string;     // Admin who assigned this tutorial
  assignedAt?: string;     // When it was assigned
  priority?: boolean;      // Show at top of list
  // Reviewer feedback/input
  understanding?: 1 | 2 | 3 | 4 | 5; // How well they understand the tag (1-5)
  notes?: string;          // Their notes/considerations about the tag
  exampleClipNotes?: Record<string, string>; // clipId -> their thoughts on each example
  comboNotes?: Record<string, string>; // "tagId-categoryId" -> thoughts on each combination
}

// Selection state for browsing
export interface ClipSelection {
  category: {
    id: string;
    name: string;
    emoji: string;
    color: string;
    area: string;
    description: string;
    instruction: string;
  };
  clip: Clip;
}

// App mode
export type AppMode = 'datenite' | 'creator' | 'reviewer' | 'recommend' | 'admin' | null;

// AI Research Question Template (editable by admin)
export interface ResearchQuestionTemplate {
  id: string;
  label: string;       // Short label for the chip/button
  question: string;    // Full question text
  context: string;     // Context shown with the question
  active: boolean;     // Whether this question is currently active
  createdAt: string;
  updatedAt?: string;
}

// User presence for real-time online tracking
export interface UserPresence {
  id: string;
  userType: 'reviewer' | 'couple';
  userId: string;      // Reviewer ID or Couple ID
  userName: string;    // Display name
  currentPage: AppMode | 'home' | 'admin';
  lastSeen: string;    // ISO timestamp
}

// ---- Admin People overview (GET /api/admin/people) ----

export interface AdminLoginStats {
  lastLoginAt?: string;
  loginCount: number;
  loginsLast30d: number;
  lastLoginCountry?: string;
  lastLoginCity?: string;
  lastLoginUserAgent?: string;
}

export interface AdminPersonRef {
  id: string;
  label: string;
}

export interface AdminCoupleSummary extends AdminLoginStats {
  coupleId: string;
  hisName: string;
  herName: string;
  setupAt: string;
  lastActivityAt?: string;
  lastSeen?: string;
  currentPage?: string;
  managedBy?: string;
  ownerReviewer?: AdminPersonRef;   // reviewer whose OWN couple profile this is
  coaches: AdminPersonRef[];        // reviewers coaching this couple (managedCoupleId)
  hasHisProfile: boolean;
  hasHerProfile: boolean;
  feedbackSubmittedAt?: string;
  surveyCompletedAt?: string;
  preferencesCount: number;
  favoritesCount: number;
  triedCount: number;
  lastTriedAt?: string;
  wishlistCount: number;
  upcomingCount: number;
  completedCount: number;
  submissionCount: number;
  pendingSubmissionCount: number;
  approvedSubmissionCount: number;
  lastSubmissionAt?: string;
  pendingRequestCount: number;
  unreadFromCoupleCount: number;
  lastMessageAt?: string;
  awaitingCoupleCount: number;      // pending follow-ups + questions sent to the couple
}

export interface AdminReviewerSummary extends AdminLoginStats {
  id: string;
  code: string;
  name?: string;
  mode?: string;
  gender?: string;
  registeredAt: string;
  lastSeen?: string;
  currentPage?: string;
  ownCouple?: AdminPersonRef;
  managedCouple?: AdminPersonRef;
  clipReviewCount: number;
  lastClipReviewAt?: string;
  submissionReviewCount: number;
  lastSubmissionReviewAt?: string;
  pendingFollowUpCount: number;
  pendingEditCount: number;
  unreadTutorialCount: number;
}

export interface AdminPeople {
  couples: AdminCoupleSummary[];
  reviewers: AdminReviewerSummary[];
  loginTrackingEnabled: boolean;    // false until d1-login-events.sql has been applied
  generatedAt: string;
}

export type AdminActivityKind =
  | 'login' | 'message' | 'coach-message' | 'tried' | 'submission' | 'scheduled' | 'completed'
  | 'answered' | 'request' | 'wishlist';

export interface AdminActivityEvent {
  at: string;
  kind: AdminActivityKind;
  title: string;          // may contain {clip}, filled from clipTitle or the app's clip list
  detail?: string;         // for 'scheduled', the planned date as yyyy-MM-dd
  clipId?: string;
  clipTitle?: string;
}

// One couple's recent history for the admin couple record
export interface AdminCoupleActivity {
  coupleId: string;
  events: AdminActivityEvent[];                         // newest first
  triedClips: { clipId: string; categoryId?: string; count: number }[];
  nextDate?: { scheduledDate: string; clipId: string; clipTitle?: string };
}

// Storage keys
export const STORAGE_KEYS = {
  CLIPS: 'vb-clips',
  REVIEWERS: 'vb-reviewers',
  CREATORS: 'vb-creators',
  DATENITE_CLIPS: 'vb-datenite-clips',
  COUPLE_DATA: 'vb-couple-data',
  RECOMMENDATIONS: 'vb-recommendations', // keyed by couple ID
  SCHEDULED_ACTIVITIES: 'vb-scheduled-activities', // keyed by couple ID (shared by couple)
  COUPLE_CHAT: 'vb-couple-chat', // keyed by couple ID - two-way chat between couple and reviewer
  COUPLE_SUBMISSIONS: 'vb-couple-submissions', // keyed by couple ID - couple's video submissions
  COUPLE_REQUESTS: 'vb-couple-requests', // keyed by couple ID - content requests
  CLIP_VIEWS: 'vb-clip-views', // tracks when reviewers view clips
  WISHLIST: 'vb-wishlist', // keyed by couple ID - couple's shared "want to try" list
  REVIEWER_FOLLOWUPS: 'vb-reviewer-followups', // custom Q&A from admin to reviewers
  COUPLE_FOLLOWUPS: 'vb-couple-followups', // AI follow-up Q&A from admin to couples
  COUPLE_QUESTIONS: 'vb-couple-questions', // questions from reviewers to couples
  TAG_TUTORIALS: 'vb-tag-tutorials', // tag education tutorials for reviewers
  RESEARCH_QUESTIONS: 'vb-research-questions', // AI research question templates
  USER_PRESENCE: 'vb-user-presence', // real-time online status tracking
  SUGGESTED_EDITS: 'vb-suggested-edits', // reviewer clip edit suggestions pending admin approval
  CLIP_SUGGESTIONS: 'vb-clip-suggestions', // curated clip suggestions from admin to couples for rating
  PICK_FOR_US: 'vb-pick-for-us', // couple picks clips for reviewer to choose from
  ACTIVITY_FOLLOWUPS: 'vb-activity-followups', // keyed by couple ID - follow-up surveys for scheduled activities
  FEMALE_SURVEY_CONFIG: 'vb-female-survey-config', // admin-configured clips for each wetness/squirting level
  FEMALE_SURVEY_RESPONSES: 'vb-female-survey-responses', // couple responses to the female visual survey
} as const;

// Bundle of couple-specific Q&A data for one tab
export interface CoupleTabData {
  coupleData: CoupleData;
  coupleFollowUps: CoupleFollowUp[];
  coupleQuestions: CoupleQuestion[];
  clipSuggestions: ClipSuggestionRequest[];
}

// Extended data for ConsolidatedQAView with tabs support
export interface ConsolidatedQAViewData {
  reviewer: Reviewer;
  reviewerFollowUps: ReviewerFollowUp[];
  clips: Clip[];
  managedCouple: CoupleTabData | null;
  ownCouple: CoupleTabData | null;
}

// Consolidated profile data for unified Q&A view and profile text export
export interface ConsolidatedProfileData {
  reviewer: Reviewer;
  coupleData: CoupleData | null;
  reviewerFollowUps: ReviewerFollowUp[];
  coupleFollowUps: CoupleFollowUp[];
  coupleQuestions: CoupleQuestion[];
  clipSuggestions: ClipSuggestionRequest[];
  clips: Clip[];
}
